import BrowserImageManipulation from 'browser-image-manipulation';
import { convertFileToBase64 } from '@/helpers/FileHelper';
import { MAX_INFERENCE_DIM } from '@/helpers/RobvisionConstants';

export { MAX_INFERENCE_DIM };

export interface DownsampledImage {
  dataUrl: string;
  width: number;
  height: number;
  originalWidth: number;
  originalHeight: number;
  scale: number;
}

function crop(
  image: File,
  width: number,
  height: number,
  left: number,
  top: number,
) {
  return new Promise((resolve) => {
    new BrowserImageManipulation()
      .loadBlob(image, {
        fixOrientation: true, // about problem: https://www.howtogeek.com/254830/why-your-photos-dont-always-appear-correctly-rotated/
      })
      .crop(width, height, left, top)
      .resize(width, height)
      .saveAsImage()
      .then((base64: string) => {
        resolve(base64);
      })
      .catch((e) => console.error(e.toString()));
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });
}

export async function downsampleImageForInference(
  file: File,
  maxDim: number = MAX_INFERENCE_DIM,
): Promise<DownsampledImage> {
  const sourceUrl = await convertFileToBase64(file);
  const img = await loadImage(sourceUrl);

  const longest = Math.max(img.naturalWidth, img.naturalHeight);
  if (longest <= maxDim) {
    return {
      dataUrl: sourceUrl,
      width: img.naturalWidth,
      height: img.naturalHeight,
      originalWidth: img.naturalWidth,
      originalHeight: img.naturalHeight,
      scale: 1,
    };
  }

  const scale = maxDim / longest;
  const targetW = Math.round(img.naturalWidth * scale);
  const targetH = Math.round(img.naturalHeight * scale);

  const canvas = document.createElement('canvas');
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to get 2D context for downsampling');
  }
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, targetW, targetH);

  return {
    dataUrl: canvas.toDataURL('image/png'),
    width: targetW,
    height: targetH,
    originalWidth: img.naturalWidth,
    originalHeight: img.naturalHeight,
    scale,
  };
}

export async function convertImageFileToPNG(file: File): Promise<File> {
  // Already a PNG — nothing to re-encode. Important for the upload flow:
  // we eagerly normalize files to PNG when they're added to the dialog so
  // f.size reflects the real upload payload. Re-encoding here would just
  // burn CPU and risk lossless-but-non-bitwise-identical output.
  if (file.type === 'image/png') {
    return file;
  }
  const dataUrl = await convertFileToBase64(file);
  const img = await loadImage(dataUrl);

  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to get canvas context');
  }
  ctx.drawImage(img, 0, 0);

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Failed to convert canvas to blob'));
        return;
      }
      resolve(
        new File([blob], file.name.replace(/\.\w+$/, '.png'), {
          type: 'image/png',
        }),
      );
    }, 'image/png');
  });
}

export default crop;
